/**
 * useWlAddonOneTimeOption — Hooks para a opção AVULSA (compra única) de add-ons
 *
 * Espelha o padrão de useWlRecurrenceOptions, mas para a tabela 1:1
 * `plan_addon_one_time_options`. Exposto somente para o painel /wl.
 *
 * Também exporta `useWlMasterOneTimeServices(search)` para alimentar o
 * <Select> de serviços avulsos do tenant master (unit_type<>'recorrente').
 *
 * Todas as chamadas passam pelas RPCs SECURITY DEFINER:
 *   - wl_list_master_one_time_services
 *   - wl_list_addon_one_time_option
 *   - wl_upsert_addon_one_time_option
 *   - wl_delete_addon_one_time_option
 *   - wl_toggle_addon_one_time_option_active
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { createLogger } from "@/lib/logger";

const log = createLogger("useWlAddonOneTimeOption");

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------
export interface WlMasterOneTimeService {
  id: string;
  name: string;
  description: string | null;
  unit_type: string | null;
  price: number;
  currency: string | null;
  is_active: boolean;
}

export interface WlAddonOneTimeOption {
  id: string;
  addon_id: string;
  uaizy_master_service_id: string;
  service_name: string | null;
  service_unit_type: string | null;
  service_price: number;
  price: number;
  setup_fee: number;
  discount_percent: number | null;
  stripe_price_id: string | null;
  is_active: boolean;
  sort_order: number;
}

// ---------------------------------------------------------------------------
// Lista de serviços avulsos do tenant master
// ---------------------------------------------------------------------------
export function useWlMasterOneTimeServices(search?: string | null) {
  const term = (search || "").trim() || null;
  return useQuery({
    queryKey: ["wl-master-one-time-services", term],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<WlMasterOneTimeService[]> => {
      const { data, error } = await supabase.rpc(
        "wl_list_master_one_time_services" as never,
        { p_search: term } as never,
      );
      if (error) {
        log.error("Erro ao listar serviços avulsos do master", { error });
        throw error;
      }
      return ((data || []) as any[]).map((s) => ({
        ...s,
        price: Number(s.price) || 0,
      })) as WlMasterOneTimeService[];
    },
  });
}

// ---------------------------------------------------------------------------
// Opção avulsa de um add-on (0 ou 1 linha)
// ---------------------------------------------------------------------------
export function useWlAddonOneTimeOption(addonId: string | null) {
  return useQuery({
    queryKey: ["wl-addon-one-time-option", addonId],
    enabled: !!addonId,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<WlAddonOneTimeOption | null> => {
      const { data, error } = await supabase.rpc(
        "wl_list_addon_one_time_option" as never,
        { p_addon_id: addonId } as never,
      );
      if (error) throw error;
      const rows = (data || []) as any[];
      if (rows.length === 0) return null;
      const o = rows[0];
      return {
        id: o.id,
        addon_id: o.addon_id,
        uaizy_master_service_id: o.uaizy_master_service_id,
        service_name: o.service_name,
        service_unit_type: o.service_unit_type,
        service_price: Number(o.service_price) || 0,
        price: Number(o.price) || 0,
        setup_fee: Number(o.setup_fee) || 0,
        discount_percent:
          o.discount_percent !== null ? Number(o.discount_percent) : null,
        stripe_price_id: o.stripe_price_id,
        is_active: !!o.is_active,
        sort_order: Number(o.sort_order) || 0,
      };
    },
  });
}

export function useWlAddonOneTimeOptionMutations(addonId: string | null) {
  const qc = useQueryClient();
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["wl-addon-one-time-option", addonId] });

  const upsert = useMutation({
    mutationFn: async (input: {
      id?: string | null;
      payload: Record<string, unknown>;
    }) => {
      const { data, error } = await supabase.rpc(
        "wl_upsert_addon_one_time_option" as never,
        { p_payload: input.payload, p_id: input.id ?? null } as never,
      );
      if (error) throw error;
      return data as string;
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc(
        "wl_delete_addon_one_time_option" as never,
        { p_id: id } as never,
      );
      if (error) throw error;
      return true;
    },
    onSuccess: invalidate,
  });

  const toggleActive = useMutation({
    mutationFn: async (input: { id: string; active: boolean }) => {
      const { error } = await supabase.rpc(
        "wl_toggle_addon_one_time_option_active" as never,
        { p_id: input.id, p_active: input.active } as never,
      );
      if (error) throw error;
      return input.active;
    },
    onSuccess: invalidate,
  });

  return { upsert, remove, toggleActive };
}
